<!-- User toggle -->
<button mat-icon-button (click)="openPanel()" #userOrigin>
    <span class="relative">
        @if (showAvatar && user?.avatar) {
            <img class="h-7 w-7 rounded-full" [src]="user.avatar" />
        }
        @if (!showAvatar || !user?.avatar) {
            <mat-icon [svgIcon]="'heroicons_outline:user-circle'"></mat-icon>
        }
        <span
            class="absolute bottom-0 right-0 h-2 w-2 rounded-full"
            [ngClass]="{
                'mb-px mr-px': !showAvatar || !user?.avatar,
                'bg-green-500': user?.status === 'online',
                'bg-amber-500': user?.status === 'away',
                'bg-red-500': user?.status === 'busy',
                'bg-gray-400': user?.status === 'not-visible',
            }"
        ></span>
    </span>
</button>

<!-- User panel -->
<ng-template #userPanel>
    <div
        class="fixed inset-0 flex flex-col overflow-hidden shadow-lg sm:static sm:inset-auto sm:w-90 sm:min-w-90 sm:rounded-2xl"
    >
        <!-- Header -->
        <div
            class="flex shrink-0 items-center bg-primary py-4 pl-6 pr-4 text-on-primary"
        >
            <div class="-ml-1 mr-3 sm:hidden">
                <button mat-icon-button (click)="closePanel()">
                    <mat-icon
                        class="text-current icon-size-5"
                        [svgIcon]="'heroicons_solid:x-mark'"
                    ></mat-icon>
                </button>
            </div>
            <!-- Avatar -->
            <div class="relative mr-3">
                @if (showAvatar && user?.avatar) {
                    <img class="h-10 w-10 rounded-full" [src]="user.avatar" />
                }
                @if (!showAvatar || !user?.avatar) {
                    <mat-icon
                        class="text-current icon-size-10"
                        [svgIcon]="'heroicons_outline:user-circle'"
                    ></mat-icon>
                }
                <span
                    class="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full ring-2 ring-primary"
                    [ngClass]="{
                        'bg-green-500': user?.status === 'online',
                        'bg-amber-500': user?.status === 'away',
                        'bg-red-500': user?.status === 'busy',
                        'bg-gray-400': user?.status === 'not-visible',
                    }"
                ></span>
            </div>
            <!-- Signed in as -->
            <div class="flex min-w-0 flex-col leading-tight">
                <div class="text-sm font-medium opacity-80">Signed in as</div>
                <div class="truncate text-lg font-medium leading-6">
                    {{ user?.email }}
                </div>
            </div>
        </div>

        <!-- Content -->
        <div
            class="bg-card relative flex flex-auto flex-col divide-y overflow-y-auto sm:max-h-120"
        >
            <!-- Roles -->
            @if (user?.userRealmRoles?.length) {
                <div class="flex flex-col px-6 py-4">
                    <div class="text-secondary text-sm font-medium leading-none">
                        Roles
                    </div>
                    <mat-chip-listbox class="mt-2 max-w-fit">
                        @for (role of user.userRealmRoles; track role) {
                            <mat-chip-option
                                class="text-xs font-thin"
                                selected
                                #tooltip="matTooltip"
                                [matTooltip]="role.definition"
                            >
                                {{ role.defaultLabel }}
                            </mat-chip-option>
                        }
                    </mat-chip-listbox>
                </div>
            }

            <!-- Profile -->
            <button
                type="button"
                class="group flex w-full items-center px-6 py-4 hover:bg-gray-50 dark:hover:bg-black dark:hover:bg-opacity-5"
            >
                <div
                    class="mr-4 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700"
                >
                    <mat-icon
                        class="icon-size-5"
                        [svgIcon]="'heroicons_outline:user-circle'"
                    ></mat-icon>
                </div>
                <span class="font-medium">Profile</span>
            </button>

            <!-- Settings -->
            <button
                type="button"
                class="group flex w-full items-center px-6 py-4 hover:bg-gray-50 dark:hover:bg-black dark:hover:bg-opacity-5"
            >
                <div
                    class="mr-4 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700"
                >
                    <mat-icon
                        class="icon-size-5"
                        [svgIcon]="'heroicons_outline:cog-8-tooth'"
                    ></mat-icon>
                </div>
                <span class="font-medium">Settings</span>
            </button>

            <!-- Status -->
            <div class="flex flex-col py-2">
                <div class="text-secondary px-6 pb-1 pt-2 text-sm font-medium">
                    Status
                </div>
                @for (
                    option of [
                        { value: 'online', label: 'Online', dot: 'bg-green-500' },
                        { value: 'away', label: 'Away', dot: 'bg-amber-500' },
                        { value: 'busy', label: 'Busy', dot: 'bg-red-500' },
                        {
                            value: 'not-visible',
                            label: 'Invisible',
                            dot: 'bg-gray-400',
                        },
                    ];
                    track option.value
                ) {
                    <button
                        type="button"
                        class="group flex w-full items-center px-6 py-2 hover:bg-gray-50 dark:hover:bg-black dark:hover:bg-opacity-5"
                        (click)="updateUserStatus(option.value)"
                    >
                        <span
                            class="mr-4 ml-1.5 inline-flex h-4 w-4 shrink-0 rounded-full"
                            [ngClass]="option.dot"
                        ></span>
                        <span
                            [ngClass]="{
                                'font-medium': user?.status === option.value,
                            }"
                        >
                            {{ option.label }}
                        </span>
                        @if (user?.status === option.value) {
                            <mat-icon
                                class="text-primary icon-size-5 ml-auto"
                                [svgIcon]="'heroicons_solid:check'"
                            ></mat-icon>
                        }
                    </button>
                }
            </div>

            <!-- Sign out -->
            <button
                type="button"
                class="group flex w-full items-center px-6 py-4 hover:bg-gray-50 dark:hover:bg-black dark:hover:bg-opacity-5"
                (click)="signOut()"
            >
                <div
                    class="mr-4 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700"
                >
                    <mat-icon
                        class="icon-size-5"
                        [svgIcon]="'heroicons_outline:arrow-right-on-rectangle'"
                    ></mat-icon>
                </div>
                <span class="font-medium">Sign out</span>
            </button>
        </div>
    </div>
</ng-template>
